import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2 as Grid,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import AppMessage from "../../components/AppMessage/AppMessage";
import IconDelete from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { validate } from "validate.js";
import {
  resetStateAdvisories,
  updateAdvisory,
  submitAdvisory,
} from "../../app/slices/advisoriesSlice";

const AdvisoryDialog = ({
  open,
  onClose,
  title,
  selectedAdvisory,
  method = "VIEW",
  readOnly = false,
}) => {
  const {
    data: dataPut,
    status: statusPut,
    error: errorPut,
  } = useSelector(({ advisories }) => advisories.put);

  const {
    data: dataPost,
    status: statusPost,
    error: errorPost,
  } = useSelector(({ advisories }) => advisories.post);

  const [formValues, setFormValues] = useState(selectedAdvisory || {});
  const [errors, setErrors] = useState([]);
  const [response, setResponse] = useState(null);
  const dispatch = useDispatch();

  const statuses = [
    { value: "A", label: "Active" },
    { value: "I", label: "Inactive" },
  ];

  const schema = {
    title: {
      presence: { allowEmpty: false },
    },
    referenceNumber: {
      presence: { allowEmpty: false },
    },
    overview: {
      presence: { allowEmpty: false },
    },
    description: {
      presence: { allowEmpty: false },
    },
    impact: {
      presence: { allowEmpty: false },
    },
    source: {
      presence: { allowEmpty: false },
    },
    affectedSoftwares: {
      presence: { allowEmpty: false },
    },
  };

  const schemaAddReference = {
    referenceTitle: {
      presence: { allowEmpty: false },
    },
    referenceUrl: {
      presence: { allowEmpty: false },
      url: true,
    },
  };

  //use effect
  useEffect(() => {
    dispatch(resetStateAdvisories(["put"]));
    dispatch(resetStateAdvisories(["post"]));
    return () => {
      dispatch(resetStateAdvisories(["put"]));
      dispatch(resetStateAdvisories(["post"]));
    };
  }, []);

  useEffect(() => {
    if (errorPut || errorPost) {
      setResponse({
        type: "error",
        message: errorPost?.message || errorPut?.message || "An error occurred",
      });
    }
  }, [errorPut, errorPost]);

  useEffect(() => {
    if (dataPut || dataPost) {
      setResponse({
        type: "success",
        message: dataPost ? "Advisory posted successfully" : dataPut,
      });
      if (method === "ADD") {
        setFormValues({});
      }
    }
  }, [dataPut, dataPost]);

  //methods
  const handleSubmit = () => {
    let _errors = validate(formValues, schema);

    if (_errors) {
      setErrors(_errors);
      return;
    } else {
      setErrors([]);
    }

    dispatch(resetStateAdvisories(["put"]));
    dispatch(resetStateAdvisories(["post"]));

    if (method === "ADD") {
      dispatch(
        submitAdvisory({
          title: formValues?.title,
          reference_number: formValues?.referenceNumber,
          description: formValues?.description,
          overview: formValues?.overview,
          source: formValues?.source,
          impact: formValues?.impact,
          affected_softwares: formValues?.affectedSoftwares,
          references: formValues?.references || [],
        })
      );
    } else if (method === "UPDATE") {
      dispatch(
        updateAdvisory({
          id: selectedAdvisory?.advisoryId,
          payload: {
            title: formValues?.title,
            reference_number: formValues?.referenceNumber,
            description: formValues?.description,
            overview: formValues?.overview,
            source: formValues?.source,
            impact: formValues?.impact,
            status: formValues?.status,
            affected_softwares: formValues?.affectedSoftwares,
            references: formValues?.references || [],
          },
        })
      );
    }
  };

  const handleChange = (e) => {
    const inputName = e?.target?.name;
    const inputValue = e?.target?.value;

    setFormValues({
      ...formValues,
      [inputName]: inputValue,
    });
  };

  const addReference = () => {
    let _errors = validate(formValues, schemaAddReference);
    if (_errors) {
      setErrors(_errors);
      return;
    } else {
      setErrors([]);
    }

    let _references = formValues?.references || [];
    _references.push({
      title: formValues?.referenceTitle,
      link: formValues?.referenceUrl,
    });

    setFormValues({
      ...formValues,
      referenceUrl: "",
      referenceTitle: "",
      references: _references,
    });
  };

  const removeReference = (index) => {
    let _references = formValues?.references;

    if (_references[index]) {
      _references.splice(index, 1);
    }

    setFormValues({
      ...formValues,
      references: _references,
    });
  };

  //builders
  const BuildForm = () => {
    return (
      <Grid container spacing={2}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            disabled={readOnly}
            fullWidth
            required
            variant="outlined"
            type="text"
            name="title"
            error={errors["title"] ? true : false}
            helperText={errors["title"] ? errors.title[0] : null}
            id="title"
            label="Title"
            onChange={handleChange}
            value={formValues?.title}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            disabled={readOnly}
            fullWidth
            required
            variant="outlined"
            type="text"
            name="overview"
            error={errors["overview"] ? true : false}
            helperText={errors["overview"] ? errors.overview[0] : null}
            id="overview"
            label="Overview"
            onChange={handleChange}
            value={formValues?.overview}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 12 }}>
          <TextField
            disabled={readOnly}
            fullWidth
            multiline
            rows={readOnly ? 8 : 5}
            required
            variant="outlined"
            type="text"
            name="description"
            error={errors["description"] ? true : false}
            helperText={errors["description"] ? errors.description[0] : null}
            id="description"
            label="Description"
            onChange={handleChange}
            value={formValues?.description}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            disabled={readOnly}
            fullWidth
            required
            variant="outlined"
            type="text"
            name="referenceNumber"
            error={errors["referenceNumber"] ? true : false}
            helperText={
              errors["referenceNumber"] ? errors.referenceNumber[0] : null
            }
            id="referenceNumber"
            label="Reference Number"
            onChange={handleChange}
            value={formValues?.referenceNumber}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            disabled={readOnly}
            fullWidth
            required
            variant="outlined"
            type="text"
            name="source"
            error={errors["source"] ? true : false}
            helperText={errors["source"] ? errors.source[0] : null}
            id="source"
            label="Source"
            onChange={handleChange}
            value={formValues?.source}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            disabled={readOnly}
            fullWidth
            required
            variant="outlined"
            type="text"
            placeholder="eg: Software One, Software Two"
            name="affectedSoftwares"
            error={errors["affectedSoftwares"] ? true : false}
            helperText={
              errors["affectedSoftwares"] ? errors.affectedSoftwares[0] : null
            }
            id="affectedSoftwares"
            label="Affected Softwares"
            onChange={handleChange}
            value={formValues?.affectedSoftwares}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            disabled={readOnly}
            fullWidth
            required
            variant="outlined"
            type="text"
            name="impact"
            error={errors["impact"] ? true : false}
            helperText={errors["impact"] ? errors.impact[0] : null}
            id="impact"
            label="Impact"
            onChange={handleChange}
            value={formValues?.impact}
          />
        </Grid>
        {(method === "UPDATE" || method === "VIEW") && (
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              disabled={readOnly}
              fullWidth
              variant="outlined"
              name="status"
              id="incident-rating"
              select
              label="Status"
              error={errors["status"] ? true : false}
              helperText={errors["status"] ? errors.status[0] : null}
              onChange={handleChange}
              value={formValues?.status}
            >
              {statuses.map((option) => {
                return <MenuItem value={option.value}>{option.label}</MenuItem>;
              })}
            </TextField>
          </Grid>
        )}
        <Grid item size={12}>
          <div className="p-20 form-darker-section">
            <Grid size={12} spacing={2} container>
              <Grid size={12}>
                <p>References</p>
              </Grid>
              {!readOnly && (
                <>
                  <Grid item size={{ xs: 12, md: 4 }}>
                    <TextField
                      disabled={readOnly}
                      fullWidth
                      required
                      variant="outlined"
                      type="text"
                      name="referenceTitle"
                      error={errors["referenceTitle"] ? true : false}
                      helperText={
                        errors["referenceTitle"]
                          ? errors.referenceTitle[0]
                          : null
                      }
                      id="referenceTitle"
                      label="Reference Title"
                      onChange={handleChange}
                      value={formValues?.referenceTitle}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 4 }}>
                    <TextField
                      disabled={readOnly}
                      fullWidth
                      required
                      variant="outlined"
                      type="text"
                      name="referenceUrl"
                      placeholder="https://somelink.ext"
                      error={errors["referenceUrl"] ? true : false}
                      helperText={
                        errors["referenceUrl"] ? errors.referenceUrl[0] : null
                      }
                      id="referenceUrl"
                      label="Reference Link"
                      onChange={handleChange}
                      value={formValues?.referenceUrl}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 4 }}>
                    <Button
                      style={{ marginTop: "5px" }}
                      id="btn-add-reference"
                      variant="contained"
                      size="small"
                      onClick={() => {
                        addReference();
                      }}
                      color="secondary"
                    >
                      + Add Reference
                    </Button>
                  </Grid>
                </>
              )}

              <Grid item size={12}>
                {formValues?.references?.length > 0 && (
                  <table className="app-simple-table">
                    <thead>
                      <th>Reference Title</th>
                      <th>Reference Link</th>
                      {!readOnly && <th>Remove</th>}
                    </thead>
                    <tbody>
                      {formValues?.references?.map((item, index) => (
                        <tr>
                          <td>{item?.title}</td>
                          <td>{item?.link}</td>
                          {!readOnly && (
                            <td>
                              <div className="icon-button">
                                <IconDelete
                                  color="error"
                                  onClick={() => {
                                    removeReference(index);
                                  }}
                                />
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {(formValues?.references?.length === 0 ||
                  formValues?.references === undefined) && (
                  <div className="flex-row p-20 justify-content-center">
                    No references added
                  </div>
                )}
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"md"}
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle id="responsive-dialog-title">
        <span id="modal-header">
          <b>{title?.toUpperCase()}</b>
        </span>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <div className="p-10">{BuildForm()}</div>
          {response && (
            <AppMessage
              isOpen={response !== null}
              message={response?.message}
              messageType={response?.type}
              onClose={() => {
                setResponse(null);
              }}
            />
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {dataPut === null &&
          dataPost === null &&
          (method === "UPDATE" || method === "ADD") && (
            <Button
              id="btn-submit"
              variant="contained"
              size="small"
              disabled={statusPut === "loading" || statusPost === "loading"}
              onClick={() => {
                handleSubmit();
              }}
              color="primary"
            >
              {(statusPut === "loading" || statusPost === "loading") && (
                <CircularProgress
                  size={20}
                  color="white"
                  style={{ marginRight: "5px" }}
                />
              )}
              {method === "ADD" ? "Post" : "Update"}
            </Button>
          )}

        <Button
          id="btn-close"
          variant="contained"
          size="small"
          onClick={() => {
            onClose(dataPut || dataPost ? true : false);
          }}
          color="error"
        >
          <CloseIcon fontSize="small" className="app-button-icon" /> Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvisoryDialog;
